import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fetchLandingMetrics } from "@/lib/api";

const zeroMetrics = {
  complaints_filed: 0,
  active_booths: 0,
  organizations: 0,
  ack_rate: 0,
};

export const HomeLandingPage = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(zeroMetrics);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchLandingMetrics();
        setMetrics({ ...zeroMetrics, ...data });
      } catch {
        setMetrics(zeroMetrics);
      }
    };
    load();
  }, []);

  return (
    <div className="landing-root" data-testid="landing-root">
      <header className="nav" data-testid="landing-header">
        <div className="logo">
          <div className="logo-mark">
            <span>A</span>
          </div>
          <span className="logo-text">Aavedak</span>
        </div>
        <nav className="nav-links" data-testid="landing-nav-links">
          <a href="#workflow" data-testid="nav-link-workflow">How it works</a>
          <a href="#channels" data-testid="nav-link-booth">Find a booth</a>
          <a href="#roles" data-testid="nav-link-organisations">For organisations</a>
          <button className="btn-outline" onClick={() => navigate("/user/login")} data-testid="landing-login-btn">
            Login
          </button>
          <button className="btn-primary" onClick={() => navigate("/user/login")} data-testid="landing-file-complaint-btn">
            File a complaint
          </button>
        </nav>
      </header>

      <main>
        <section className="hero" data-testid="landing-hero">
          <div className="hero-eyebrow">Trusted Grievance Platform · National Portal</div>
          <h1>
            Your voice, <em>formally recorded.</em>
            <br />
            Every complaint, tracked to resolution.
          </h1>
          <p>
            Aavedak converts grievances into structured AI-drafted applications — file online
            instantly or visit physical booth centers for assisted filing.
          </p>

          <div className="hero-actions" data-testid="landing-hero-actions">
            <button className="btn-lg btn-saffron-lg" onClick={() => navigate("/user/login")} data-testid="hero-file-btn">
              File a complaint
            </button>
            <button className="btn-lg btn-outline-lg" onClick={() => navigate("/boothlogin")} data-testid="hero-booth-btn">
              Find a booth
            </button>
            <button className="btn-lg btn-outline-lg" onClick={() => navigate("/businesslogin")} data-testid="hero-business-btn">
              Partner departments
            </button>
          </div>

          <div className="stat-row" data-testid="landing-stat-row">
            <div className="stat-item">
              <div className="stat-num">{metrics.complaints_filed}</div>
              <div className="stat-label">Complaints filed</div>
            </div>
            <div className="stat-item">
              <div className="stat-num">{metrics.active_booths}</div>
              <div className="stat-label">Active booths</div>
            </div>
            <div className="stat-item">
              <div className="stat-num">{metrics.organizations}</div>
              <div className="stat-label">Organisations onboarded</div>
            </div>
            <div className="stat-item">
              <div className="stat-num">{metrics.ack_rate}%</div>
              <div className="stat-label">Acknowledgement rate</div>
            </div>
          </div>
        </section>

        <section className="trust-bar" data-testid="landing-trust-bar">
          <div className="trust-item">
            <div className="trust-icon">🤖</div>
            <div className="trust-text">
              <p>AI-Guided Drafting</p>
              <span>Speak naturally; AI translates grievance into formal applications.</span>
            </div>
          </div>
          <div className="trust-item">
            <div className="trust-icon">🔒</div>
            <div className="trust-text">
              <p>Immutable Submissions</p>
              <span>Complaint records stay traceable and audit-friendly.</span>
            </div>
          </div>
          <div className="trust-item">
            <div className="trust-icon">📈</div>
            <div className="trust-text">
              <p>Lifecycle Tracking</p>
              <span>Stage-by-stage transparency from intake to closure.</span>
            </div>
          </div>
          <div className="trust-item">
            <div className="trust-icon">🖨️</div>
            <div className="trust-text">
              <p>Booth-Assisted Filing</p>
              <span>Walk-in support at kiosks for assisted print-ready filing.</span>
            </div>
          </div>
        </section>

        <section className="section" id="workflow" data-testid="landing-workflow-section">
          <div className="section-label">Operational Model</div>
          <div className="section-title">File securely in four steps</div>
          <div className="section-sub">Simple pathways for self-service filing and booth-assisted support.</div>

          <div className="steps-grid">
            {[
              ["1", "One-Time Registration", "Register once with mobile and email for authenticated access."],
              ["2", "Interactive AI Interview", "Guided follow-ups gather complete legal/factual details."],
              ["3", "Route & Target Select", "Map case to booth, organization and authority destination."],
              ["4", "Instant Tracking Receipt", "Get complaint reference via SMS and PDF copy by email."],
            ].map(([step, title, body]) => (
              <div className="step-card" key={step} data-testid={`landing-step-${step}`}>
                <div className="step-num">{step}</div>
                <div className="step-title">{title}</div>
                <div className="step-body">{body}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="section channels-section" id="channels" data-testid="landing-channels-section">
          <div className="section-label">Access Channels</div>
          <div className="section-title">Flexible portals optimized per role</div>
          <div className="section-sub">Standardized tools with complete processing transparency.</div>

          <div className="channels">
            <div className="channel-card" data-testid="online-channel-card">
              <div className="channel-icon">🌐</div>
              <div className="channel-title">Online Citizen Filing</div>
              <div className="channel-body">Login from home and submit complaints through guided AI intake.</div>
              <button type="button" className="ch-link" onClick={() => navigate("/user/login")} data-testid="online-channel-link">
                Get started online
              </button>
            </div>
            <div className="channel-card" data-testid="booth-channel-card">
              <div className="channel-icon">🏢</div>
              <div className="channel-title">Assisted Kiosk Booths</div>
              <div className="channel-body">Incharge panels support assisted filing and physical print dispatch.</div>
              <button type="button" className="ch-link" onClick={() => navigate("/boothlogin")} data-testid="booth-channel-link">
                Open booth login
              </button>
            </div>
          </div>
        </section>

        <section className="logins-section" id="roles" data-testid="landing-roles-section">
          <div className="section-label">Gateway Access Points</div>
          <div className="section-title">Multi-tenant role-based routing</div>
          <div className="section-sub">Separate endpoints designed for each actor group.</div>

          <div className="login-grid">
            {[
              ["Applicant", "/user/login", "Mobile / OTP", "/user/login"],
              ["Booth Interface", "/login", "Booth Credentials", "/login"],
              ["Booth Incharge", "/boothlogin", "Incharge Panel", "/boothlogin"],
              ["Organization Admin", "/businesslogin", "Org Identifier", "/businesslogin"],
              ["Support Desk", "/supportlogin", "Ops Control", "/supportlogin"],
              ["Platform Admin", "/adminlogin", "Secure Vault Access", "/adminlogin"],
            ].map(([label, route, badge, target], index) => (
              <button
                type="button"
                className="login-card"
                key={label}
                onClick={() => navigate(target)}
                data-testid={`landing-login-card-${index}`}
              >
                <i>🔐</i>
                <div className="login-role">{label}</div>
                <div className="login-url">{route}</div>
                <div className="badge">{badge}</div>
              </button>
            ))}
          </div>
        </section>
      </main>

      <footer className="footer" data-testid="landing-footer">
        <div className="logo">
          <div className="logo-mark">
            <span>A</span>
          </div>
          <span className="logo-text footer-logo">Aavedak Core</span>
        </div>
        <p>© 2026 Aavedak · National Public Grievance Intake Protocol</p>
      </footer>

      <div className="landing-quick-access" data-testid="landing-quick-access">
        <Button className="quick-access-btn" onClick={() => navigate("/test-users")} data-testid="view-test-users-btn">
          View Test Users
        </Button>
      </div>
    </div>
  );
};
